import { useAuth } from './useAuth'
import { detectBrowserTimezone } from '@/lib/timezones'

/**
 * Returns the current user's IANA timezone string for display formatting.
 *
 * Fallback chain:
 *   1. User's saved profile.timezone (set during signup or in Settings → Profile)
 *   2. Org's settings.timezone (set during onboarding)
 *   3. Browser-detected (Intl)
 *   4. America/Los_Angeles (last resort)
 *
 * Use this for ANY user-facing event timestamp: bookings, calls, chats, tasks,
 * notifications. Do NOT use it for analytics heatmaps or business-hours math —
 * those are about firm-side operations and should stay in the firm's timezone.
 */
export function useUserTimezone(): string {
  const { agent, organization } = useAuth() as any
  return (
    agent?.timezone ||
    organization?.settings?.timezone ||
    detectBrowserTimezone()
  )
}

/**
 * Format a Date or ISO string as a wall-clock time in the user's timezone.
 * Pass options to override defaults (defaults to "h:mm a", e.g. "3:25 PM").
 */
export function formatTimeInUserTz(
  date: Date | string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true },
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleTimeString('en-US', { timeZone: timezone, ...options })
}

/**
 * Format a Date or ISO string as a wall-clock date in the user's timezone.
 */
export function formatDateInUserTz(
  date: Date | string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' },
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', { timeZone: timezone, ...options })
}

/**
 * Return an ISO YYYY-MM-DD string for the given instant rendered in the given
 * timezone. Stable for day-grouping (e.g. grouping chat messages by day).
 */
export function toIsoDateInUserTz(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-CA', { timeZone: timezone })
}

/**
 * True when the given instant falls on today's date in the user's timezone.
 */
export function isTodayInUserTz(date: Date | string, timezone: string): boolean {
  return toIsoDateInUserTz(date, timezone) === toIsoDateInUserTz(new Date(), timezone)
}

/**
 * True when the given instant falls on yesterday's date in the user's timezone.
 */
export function isYesterdayInUserTz(date: Date | string, timezone: string): boolean {
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
  return toIsoDateInUserTz(date, timezone) === toIsoDateInUserTz(yesterday, timezone)
}

/**
 * Convert a wall-clock date+time string ("YYYY-MM-DD" + "HH:mm") in the given
 * IANA timezone to a real UTC Date. Use this whenever a user picks a date/time
 * in a form and you need to send a UTC ISO string to the API — the naive
 * `new Date(\`${date}T${time}:00\`)` interprets the string in the BROWSER's
 * timezone, which is wrong when the user's IANA tz differs from their browser
 * (per `feedback_timezone_dates.md`).
 */
export function zonedDateTimeToUtc(
  dateStr: string,
  timeStr: string,
  timezone: string,
): Date {
  const [y, mo, d] = dateStr.split('-').map(Number)
  const [h, mi] = timeStr.split(':').map(Number)
  const utcCandidate = new Date(Date.UTC(y, mo - 1, d, h, mi, 0))
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const parts: Record<string, string> = {}
  for (const p of fmt.formatToParts(utcCandidate)) parts[p.type] = p.value
  const seenHour = parts.hour === '24' ? 0 : parseInt(parts.hour)
  const seenAsUtc = Date.UTC(
    parseInt(parts.year),
    parseInt(parts.month) - 1,
    parseInt(parts.day),
    seenHour,
    parseInt(parts.minute),
  )
  const offset = utcCandidate.getTime() - seenAsUtc
  return new Date(utcCandidate.getTime() + offset)
}

/**
 * Returns true if the two timezones currently produce different wall-clock
 * times for the same instant. Used to decide whether to show a "(X at firm)"
 * hint when a user's TZ differs from the firm's.
 */
export function timezonesDiffer(a: string, b: string): boolean {
  if (!a || !b || a === b) return false
  try {
    const now = new Date()
    const aHour = parseInt(now.toLocaleString('en-US', { timeZone: a, hour: '2-digit', hour12: false }))
    const bHour = parseInt(now.toLocaleString('en-US', { timeZone: b, hour: '2-digit', hour12: false }))
    return aHour !== bHour
  } catch {
    return false
  }
}
